/**
 * sqlite3mt remote-head tracking helpers
 *
 * Mechanical extraction from §96 §13.
 *
 * This module owns the Control Worker-side contract for:
 * - runtimeEpoch-stamped `get-head-versioned` bridge requests
 * - stale-response rejection by runtimeEpoch / reqSeq
 * - monotone remoteHead tracking via canonical root identity
 */

import {
    sameRoot,
    type PublicHead,
    type Root,
} from './types'

export const PROTOCOL_VIOLATION_SAME_VERSION_DIFFERENT_ROOT =
    'PROTOCOL_VIOLATION_SAME_VERSION_DIFFERENT_ROOT'
export const PROTOCOL_VIOLATION_REMOTE_VERSION_REWIND =
    'PROTOCOL_VIOLATION_REMOTE_VERSION_REWIND'
export const REMOTE_HEAD_PROTOCOL_VIOLATION = 'REMOTE_HEAD_PROTOCOL_VIOLATION'

export interface RemoteHeadTrackingState {
    remoteHead: PublicHead
    remoteRefreshReqSeq: bigint
    remoteRefreshAppliedReqSeq: bigint
}

export interface GetHeadVersionedRequest {
    type: 'get-head-versioned'
    runtimeEpoch: bigint
    reqSeq: bigint
}

export interface RemoteHeadResponseMessage {
    type: 'remote-head-response'
    runtimeEpoch: bigint
    reqSeq: bigint
    root: Root
    version: bigint
}

export interface RemoteHeadHooks {
    maybeStartPushOrImport(): void
    enterFatalRuntime(error: unknown, reason: string): void
}

export type ApplyRemoteHeadResult =
    | { kind: 'IGNORED_OLDER' }
    | { kind: 'IGNORED_SAME' }
    | { kind: 'ADVANCED' }
    | { kind: 'FATAL' }

export type HandleRemoteHeadResponseResult =
    | { kind: 'IGNORED_STALE_EPOCH' }
    | { kind: 'IGNORED_STALE_REQ' }
    | ApplyRemoteHeadResult

export function refreshRemoteHead(
    state: Pick<RemoteHeadTrackingState, 'remoteRefreshReqSeq'>,
    runtimeEpoch: bigint,
    postMessage: (msg: GetHeadVersionedRequest) => void
): bigint {
    const reqSeq = state.remoteRefreshReqSeq + 1n
    state.remoteRefreshReqSeq = reqSeq
    postMessage({
        type: 'get-head-versioned',
        runtimeEpoch,
        reqSeq,
    })
    return reqSeq
}

export function applyRemoteHeadIfNewer(
    state: Pick<RemoteHeadTrackingState, 'remoteHead' | 'remoteRefreshAppliedReqSeq'>,
    root: Root,
    version: bigint,
    sourceReqSeq: bigint,
    hooks: RemoteHeadHooks
): ApplyRemoteHeadResult {
    if (version < state.remoteHead.version) {
        if (!sameRoot(root, state.remoteHead.root)) {
            hooks.enterFatalRuntime(
                PROTOCOL_VIOLATION_REMOTE_VERSION_REWIND,
                REMOTE_HEAD_PROTOCOL_VIOLATION
            )
            return { kind: 'FATAL' }
        }
        return { kind: 'IGNORED_OLDER' }
    }

    if (version === state.remoteHead.version) {
        if (sameRoot(root, state.remoteHead.root)) {
            state.remoteRefreshAppliedReqSeq = maxBigInt(
                state.remoteRefreshAppliedReqSeq,
                sourceReqSeq
            )
            return { kind: 'IGNORED_SAME' }
        }

        hooks.enterFatalRuntime(
            PROTOCOL_VIOLATION_SAME_VERSION_DIFFERENT_ROOT,
            REMOTE_HEAD_PROTOCOL_VIOLATION
        )
        return { kind: 'FATAL' }
    }

    state.remoteHead = { root, version }
    state.remoteRefreshAppliedReqSeq = maxBigInt(
        state.remoteRefreshAppliedReqSeq,
        sourceReqSeq
    )
    hooks.maybeStartPushOrImport()
    return { kind: 'ADVANCED' }
}

export function onRemoteHeadResponse(
    state: Pick<RemoteHeadTrackingState, 'remoteHead' | 'remoteRefreshAppliedReqSeq'>,
    runtimeEpoch: bigint,
    msg: RemoteHeadResponseMessage,
    hooks: RemoteHeadHooks
): HandleRemoteHeadResponseResult {
    if (msg.runtimeEpoch !== runtimeEpoch) {
        return { kind: 'IGNORED_STALE_EPOCH' }
    }

    if (msg.reqSeq < state.remoteRefreshAppliedReqSeq) {
        return { kind: 'IGNORED_STALE_REQ' }
    }

    return applyRemoteHeadIfNewer(state, msg.root, msg.version, msg.reqSeq, hooks)
}

function maxBigInt(a: bigint, b: bigint): bigint {
    return a > b ? a : b
}
